import { useMemo, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { ModelLayer } from '@dbt-labs/dbt-dag';
import { DropdownOption, Heading } from '@dbt-labs/sourdough';

import {
  AccessLevel,
  HealthStatus,
  PopularModelFilter,
  PopularModelSort,
  PopularModelSortField,
  SortDirection,
} from '@/__generated__/graphql';
import { AccessLevelFilterDropdown } from '@/components/ModelFilters/AccessLevelFilterDropdown';
import {
  AccountGroupFilterDropdown,
  GROUP_PARAM,
} from '@/components/ModelFilters/GroupFilterDropdown';
import {
  HEALTH_FILTER_PARAM,
  HealthFilterDropdown,
} from '@/components/ModelFilters/HealthFilterDropdown';
import {
  MODELING_LAYER_PARAM,
  ModelingLayerFilterDropdown,
} from '@/components/ModelFilters/ModelingLayerFilterDropdown';
import { useDbtCloudAccount } from '@/context/DbtCloudProvider';
import { usePopularModels } from '@/hooks/discovery/popularModels';
import { ResourceByEnvironmentParams } from '@/hooks/discovery/resource';
import { useScrollToElement } from '@/hooks/useScrollToElement';
import { INITIAL_PAGE_SIZE } from '../project/resource/components/ResourceFilterTable';
import { PopularModel } from './PopularModelsTable';
import { PopularModelsTable } from './PopularModelsTable';

interface PopularModelsSectionProps {
  setSelectedResourceParams: (params: ResourceByEnvironmentParams) => void;
  setIsColumnsPanelVisible: (isVisible: boolean) => void;
}

export const ALL_MODELS_FRAGMENT = 'all-models-table';
export const PopularModelsSection = ({
  setSelectedResourceParams,
  setIsColumnsPanelVisible,
}: PopularModelsSectionProps) => {
  const pageSize = INITIAL_PAGE_SIZE;
  const location = useLocation();
  const sectionRef = useRef<HTMLDivElement | null>(null);

  const { hasProjectAccess, accountId } = useDbtCloudAccount();
  const [searchParams] = useSearchParams();

  const filter = useMemo((): Partial<PopularModelFilter> => {
    return {
      modelLayer: searchParams.get(MODELING_LAYER_PARAM) as ModelLayer | undefined,
      access: searchParams.get('access') as AccessLevel | undefined,
      group: searchParams.get(GROUP_PARAM) as string | undefined,
      health: searchParams.get(HEALTH_FILTER_PARAM) as HealthStatus | undefined,
    };
  }, [searchParams]);

  const sortInfo = useMemo((): PopularModelSort => {
    return {
      direction:
        (searchParams.get('popularModelsSortDir') as SortDirection) ||
        SortDirection.Desc,
      field:
        (searchParams.get('popularModelsSortField') as PopularModelSortField) ||
        PopularModelSortField.QueryUsage30d,
    };
  }, [searchParams]);

  const popularModelsQuery = usePopularModels({
    accountId,
    first: pageSize,
    sort: sortInfo,
    filter,
  });

  // Check if the URL has the specific fragment and data is loaded
  const hasPopularModelsFragment = location.hash === `#${ALL_MODELS_FRAGMENT}`;
  const shouldScroll = hasPopularModelsFragment && !popularModelsQuery.fetching;

  useScrollToElement(sectionRef as React.RefObject<HTMLElement>, {
    enabled: shouldScroll,
  });

  const popularModels = useMemo((): PopularModel[] => {
    const models = (popularModelsQuery.data || []).map((popularModel) => ({
      ...popularModel,
      hasAccess: !popularModel.projectId || hasProjectAccess(popularModel.projectId),
    }));

    return models;
  }, [hasProjectAccess, popularModelsQuery.data]);

  const pageOptions: DropdownOption[] = [
    { label: '5 Rows', value: 5 },
    { label: '10 Rows', value: 10 },
    { label: '25 Rows', value: 25 },
    { label: '50 Rows', value: 50 },
  ];

  // Determine if we're in initial load or loading more data
  const isLoadingMore = popularModelsQuery.fetching;
  const isInitialLoad = isLoadingMore && popularModelsQuery.data === undefined;

  return (
    <div
      className="mt-12 flex flex-col space-y-4"
      id={ALL_MODELS_FRAGMENT}
      ref={sectionRef}
    >
      <Heading size="4">All models</Heading>
      <div className="my-2 flex flex-row justify-between gap-2">
        <div className="flex flex-1 flex-wrap gap-2">
          <ModelingLayerFilterDropdown />
          <AccessLevelFilterDropdown label="Model access" />
          <HealthFilterDropdown />
          <AccountGroupFilterDropdown />
        </div>
      </div>
      <PopularModelsTable
        paginationType="loadMore"
        popularModels={popularModels}
        pageOptions={pageOptions}
        pageSize={pageSize}
        isLoading={isInitialLoad}
        isLoadingMore={isLoadingMore}
        hasMoreResults={popularModelsQuery.pageInfo?.hasNextPage || false}
        fetchMore={popularModelsQuery.fetchMore}
        setSelectedResourceParams={setSelectedResourceParams}
        setIsColumnsPanelVisible={setIsColumnsPanelVisible}
        totalCount={popularModelsQuery.pageInfo?.totalCount || 0}
      />
    </div>
  );
};
